/**
 * Completeness Tracker Component
 * AI-powered discovery session completeness tracking
 * Shows weighted scores, readiness status, and gaps
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { 
  CheckCircle2, 
  AlertCircle, 
  Clock, 
  TrendingUp,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';

import { formatCategoryLabel } from '../utils/normalizeAcronyms';

interface CompletenessTrackerProps {
  companyId: string;
  accessToken: string;
  onRefresh?: () => void;
}

interface CategoryCompleteness {
  category: string;
  score: number;
  readiness: 'ready' | 'needs_more' | 'insufficient';
  isComplete: boolean;
  canProceed: boolean;
  lastUpdated: string;
}

interface CompletnessSummary {
  companyId: string;
  categories: CategoryCompleteness[];
  overall: {
    totalCategories: number;
    completeCategories: number;
    averageScore: number;
    readiness: 'ready' | 'needs_more' | 'insufficient';
    canProceedToSOW: boolean;
  };
  lastCalculated: string;
}

export function CompletenessTracker({ 
  companyId, 
  accessToken,
  onRefresh 
}: CompletenessTrackerProps) {
  const [summary, setSummary] = useState<CompletnessSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadSummary();
  }, [companyId]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/questions/completeness/${companyId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Ensure data structure is valid
        if (data && typeof data === 'object') {
          // Ensure categories is always an array
          if (!data.categories) {
            data.categories = [];
          }
          // Ensure overall exists
          if (!data.overall) {
            data.overall = {
              totalCategories: 0,
              completeCategories: 0,
              averageScore: 0,
              readiness: 'insufficient',
              canProceedToSOW: false,
            };
          }
          setSummary(data);
        }
      } else {
        console.error('Failed to load completeness summary:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading completeness summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSummary();
    setRefreshing(false);
    onRefresh?.();
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const getReadinessBadge = (readiness: string) => {
    switch (readiness) {
      case 'ready':
        return <Badge className="bg-green-500">Ready</Badge>;
      case 'needs_more':
        return <Badge className="bg-amber-500">Needs More Info</Badge>;
      case 'insufficient':
        return <Badge variant="destructive">Insufficient</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Discovery Completeness</CardTitle>
          <CardDescription>AI-powered completeness analysis</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="size-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  if (!summary || !summary.categories || summary.categories.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Discovery Completeness</CardTitle>
          <CardDescription>No discovery data yet</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600">
            Completeness tracking will appear once the client starts answering discovery questions.
          </p>
        </CardContent>
      </Card>
    );
  }

  const { overall, categories } = summary;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Discovery Completeness
              {getReadinessBadge(overall.readiness)}
            </CardTitle>
            <CardDescription>
              AI-powered analysis of discovery session quality
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RefreshCw className="size-4" />
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Summary */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm">Overall Completeness</span>
            <span className={`font-bold ${getScoreColor(overall.averageScore)}`}>
              {overall.averageScore}%
            </span>
          </div>
          <Progress value={overall.averageScore} className="h-2" />
          
          <div className="grid grid-cols-3 gap-4 pt-2">
            <div className="text-center">
              <div className="text-2xl font-bold">{overall.totalCategories}</div>
              <div className="text-xs text-slate-600">Categories</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{overall.completeCategories}</div>
              <div className="text-xs text-slate-600">Complete</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-slate-600">
                {overall.totalCategories - overall.completeCategories}
              </div>
              <div className="text-xs text-slate-600">In Progress</div>
            </div>
          </div>
        </div>

        {/* SOW Readiness Indicator */}
        {overall.canProceedToSOW ? (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle2 className="size-5 text-green-600 shrink-0" />
            <div className="text-sm">
              <div className="font-medium text-green-900">Ready for SOW Generation</div>
              <div className="text-green-700">Discovery is complete enough to proceed</div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <Clock className="size-5 text-amber-600 shrink-0" />
            <div className="text-sm">
              <div className="font-medium text-amber-900">More Discovery Needed</div>
              <div className="text-amber-700">Complete required questions before generating SOW</div>
            </div>
          </div>
        )}

        {/* Category Breakdown */}
        <div className="space-y-2">
          <h4 className="font-medium text-sm">Category Breakdown</h4>
          {categories.map((cat) => (
            <Collapsible 
              key={cat.category}
              open={expandedCategories.has(cat.category)}
            >
              <div className="border rounded-lg p-3 space-y-2">
                <CollapsibleTrigger
                  onClick={() => toggleCategory(cat.category)}
                  className="w-full flex items-center justify-between hover:bg-slate-50 -m-3 p-3 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {expandedCategories.has(cat.category) ? (
                      <ChevronDown className="size-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="size-4 text-slate-400" />
                    )}
                    <div className="text-left">
                      <div className="font-medium capitalize">
                        {formatCategoryLabel(cat.category)}
                      </div>
                      <div className="text-xs text-slate-600">
                        {cat.isComplete ? (
                          <span className="text-green-600">✓ Complete</span>
                        ) : (
                          <span>In Progress</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getReadinessBadge(cat.readiness)}
                    <span className={`font-bold ${getScoreColor(cat.score)}`}>
                      {cat.score}%
                    </span>
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent className="pt-2">
                  <div className="space-y-2 pl-7">
                    <Progress value={cat.score} className="h-1.5" />
                    <div className="flex items-center justify-between text-xs text-slate-600">
                      <span>Last updated</span>
                      <span>{new Date(cat.lastUpdated).toLocaleDateString()}</span>
                    </div>
                    {cat.canProceed ? (
                      <div className="flex items-center gap-1 text-xs text-green-700">
                        <CheckCircle2 className="size-3" />
                        Can proceed to implementation
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-amber-700">
                        <AlertCircle className="size-3" />
                        Needs more information
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>

        {/* Timestamp */}
        <div className="text-xs text-slate-500 text-center pt-2 border-t">
          Last calculated: {new Date(summary.lastCalculated).toLocaleString()}
        </div>
      </CardContent>
    </Card>
  );
}